import { faBracketsCurly } from '@fortawesome/pro-solid-svg-icons';
import React, { useCallback, useRef, useState } from 'react';
import { Icon, useThemeColors, useTranslation } from '../..';
import type { AvailableVariable } from '../../services/templateContextAnalyzer';
import { ContextAwareMergeVariableDropdown } from './ContextAwareMergeVariableDropdown';

interface TemplateVariable {
	name: string;
	type: string;
	required: boolean;
	defaultValue?: unknown;
	description?: string;
}

interface PlainTextTemplateEditorProps {
	value: string;
	onChange: (text: string) => void;
	placeholder?: string;
	minHeight?: string;
	availableVariables?: Array<TemplateVariable>;
	/** Context-aware variables with type information for nested template support */
	contextAwareVariables?: Array<AvailableVariable>;
	onInsertVariable?: (variableName: string) => void;
	className?: string;
	disabled?: boolean;
	rows?: number;
}

/**
 * A plain text template editor with merge variable insertion support.
 * Similar to RichTextEditor but for plain text templates (SMS, etc.)
 * where HTML formatting is not supported.
 */
export const PlainTextTemplateEditor: React.FC<
	PlainTextTemplateEditorProps
> = ({
	value,
	onChange,
	placeholder = '',
	minHeight = '200px',
	availableVariables = [],
	contextAwareVariables,
	onInsertVariable,
	className = '',
	disabled = false,
	rows = 8,
}) => {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	const [showVariablePicker, setShowVariablePicker] = useState(false);
	const [cursorPosition, setCursorPosition] = useState(0);

	const insertVariable = useCallback(
		(variableName: string): void => {
			const textArea = textAreaRef.current;

			if (textArea != null) {
				const start = textArea.selectionStart;
				const end = textArea.selectionEnd;
				const variableText = `{{${variableName}}}`;
				const newText =
					value.substring(0, start) + variableText + value.substring(end);
				onChange(newText);

				// Restore focus and set cursor position after variable
				setTimeout(() => {
					textArea.focus();
					textArea.setSelectionRange(
						start + variableText.length,
						start + variableText.length,
					);
				}, 0);
			}

			setShowVariablePicker(false);

			if (onInsertVariable != null) {
				onInsertVariable(variableName);
			}
		},
		[value, onChange, onInsertVariable],
	);

	// Handler for context-aware variable insertion
	const handleContextAwareInsert = useCallback(
		(text: string, cursorOffset?: number): void => {
			const textArea = textAreaRef.current;

			if (textArea != null) {
				const start = textArea.selectionStart;
				const end = textArea.selectionEnd;
				const newText = value.substring(0, start) + text + value.substring(end);
				onChange(newText);

				// Calculate new cursor position
				let newCursorPos: number;

				if (cursorOffset != null) {
					// For block syntax, position cursor inside the block
					newCursorPos = start + cursorOffset;
				} else {
					// For simple variables, position after the inserted text
					newCursorPos = start + text.length;
				}

				// Restore focus and set cursor position
				setTimeout(() => {
					textArea.focus();
					textArea.setSelectionRange(newCursorPos, newCursorPos);
					setCursorPosition(newCursorPos);
				}, 0);
			}
		},
		[value, onChange],
	);

	// Track cursor position changes
	const handleCursorChange = useCallback(() => {
		const textArea = textAreaRef.current;

		if (textArea != null) {
			setCursorPosition(textArea.selectionStart);
		}
	}, []);

	const hasSimpleVariables = availableVariables.length > 0;
	const hasContextAwareVariables =
		contextAwareVariables != null && contextAwareVariables.length > 0;

	return (
		<div
			className={`border rounded-lg overflow-hidden flex flex-col ${colors.classes.borderDefault} ${className}`}
		>
			{/* Toolbar - Context-Aware Mode */}
			{!disabled && hasContextAwareVariables && (
				<div
					className={`flex-shrink-0 flex items-center gap-1 p-2 border-b ${colors.classes.bgSecondary} ${colors.classes.borderDefault}`}
				>
					<ContextAwareMergeVariableDropdown
						rootVariables={contextAwareVariables}
						content={value}
						cursorPosition={cursorPosition}
						onInsert={handleContextAwareInsert}
						disabled={disabled}
					/>

					<div className="flex-1" />

					<span className={`text-xs ${colors.classes.textSecondary}`}>
						{t('Click to insert a variable at cursor position')}
					</span>
				</div>
			)}

			{/* Toolbar - Simple Mode (legacy) */}
			{!disabled && hasSimpleVariables && !hasContextAwareVariables && (
				<div
					className={`flex-shrink-0 flex items-center gap-1 p-2 border-b ${colors.classes.bgSecondary} ${colors.classes.borderDefault}`}
				>
					{/* Merge Variables Button */}
					<div className="relative">
						<button
							type="button"
							onClick={() => {
								setShowVariablePicker(!showVariablePicker);
							}}
							className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${colors.classes.textPrimary} hover:${colors.classes.bgSecondary}`}
							title={t('Insert Merge Variable')}
						>
							<Icon icon={faBracketsCurly} size="1x" />
							<span className="text-sm">{t('Insert Variable')}</span>
						</button>
						{showVariablePicker && (
							<div
								className={`absolute top-full left-0 mt-1 p-2 rounded shadow-lg z-10 min-w-64 max-h-64 overflow-y-auto ${colors.classes.bgPrimary} ${colors.classes.borderDefault} border`}
							>
								<div className="space-y-1">
									{availableVariables.map((variable, index) => (
										<button
											key={index}
											type="button"
											onClick={() => {
												insertVariable(variable.name);
											}}
											className={`w-full text-left px-2 py-1.5 text-sm rounded transition-colors ${colors.classes.textPrimary} hover:${colors.classes.bgSecondary}`}
											title={variable.description ?? ''}
										>
											<div className="flex items-center justify-between gap-2">
												<span className="font-mono text-xs">{`{{${variable.name}}}`}</span>
												{variable.required && (
													<span
														className={`text-xs px-1.5 py-0.5 rounded ${colors.classes.calloutWarning} ${colors.classes.textWarning}`}
													>
														{t('Required')}
													</span>
												)}
											</div>
											{variable.description != null &&
												variable.description.length > 0 && (
													<div
														className={`text-xs mt-0.5 ${colors.classes.textSecondary}`}
													>
														{variable.description}
													</div>
												)}
										</button>
									))}
								</div>
							</div>
						)}
					</div>

					<div className="flex-1" />

					<span className={`text-xs ${colors.classes.textSecondary}`}>
						{t('Click to insert a variable at cursor position')}
					</span>
				</div>
			)}

			{/* No variables hint */}
			{!disabled && !hasSimpleVariables && !hasContextAwareVariables && (
				<div
					className={`flex-shrink-0 p-2 border-b ${colors.classes.bgSecondary} ${colors.classes.borderDefault}`}
				>
					<div
						className={`flex items-center gap-2 text-xs ${colors.classes.textSecondary}`}
					>
						<Icon icon={faBracketsCurly} size="1x" />
						<span>
							{t(
								'Add variables in the panel on the right to enable merge variable insertion',
							)}
						</span>
					</div>
				</div>
			)}

			{/* TextArea */}
			<textarea
				ref={textAreaRef}
				value={value}
				onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
					onChange(e.target.value);
					handleCursorChange();
				}}
				onSelect={handleCursorChange}
				onClick={handleCursorChange}
				onKeyUp={handleCursorChange}
				placeholder={placeholder}
				disabled={disabled}
				rows={rows}
				className={`flex-1 p-4 resize-none focus:outline-none focus:ring-0 border-0 ${colors.classes.bgPrimary} ${colors.classes.textPrimary} ${disabled ? 'opacity-75 cursor-not-allowed' : ''}`}
				style={{ minHeight }}
			/>

			{/* Click outside handler to close dropdown */}
			{showVariablePicker && (
				<div
					className="fixed inset-0 z-0"
					onClick={() => {
						setShowVariablePicker(false);
					}}
				/>
			)}
		</div>
	);
};
